import { GoodsHolder, Shipment } from '../../../generated/graphql'
import {
  date,
  milestoneDate,
  shipmentStatus,
  unique,
  measure,
} from '../../../common/utils/formatter'
import { get, propertyAccessor } from '../../../common/components/properties/Properties'
import { isHold, isRebooking } from '../ShipmentFlags'
import { getShippingPointName } from '../ShippingPoints'
import { getPlannedDeliveryDate, getRebookingDate } from '../detail/milestones/ShipmentMilestones'
import { XlsxColumn } from '../../../common/components/export/ExportService'

function findCarrier(carriers, vendorCode, hubCode) {
  return carriers?.find(
    (c) => c.carrierAccountCode === vendorCode && (!hubCode || c.hubCode === hubCode)
  )
}

export const shipmentXlsxColumns: XlsxColumn[] = [
  {
    header: 'Sales Order',
    toStringFunction: (s: Shipment) => unique(get(s, 'deliveries.salesOrderNumber')),
  },
  {
    header: 'Order Type',
    toStringFunction: (s: Shipment) => unique(get(s, 'deliveries.nikeSalesOrderTypeCode')),
  },
  {
    header: 'Purchase Order Number',
    toStringFunction: (s: Shipment) => unique(get(s, 'deliveries.purchaseOrderNumber')),
  },
  {
    header: 'Shipping Point',
    toStringFunction: (s: Shipment) => s.shippingPointCode,
  },
  {
    header: 'Shipping Point Name',
    toStringFunction: (s: Shipment) => getShippingPointName(Number(s.shippingPointCode)),
  },
  {
    header: 'Ship To',
    toStringFunction: propertyAccessor('shipmentShipToAddress.shipToCode'),
  },
  {
    header: 'Ship To Name',
    toStringFunction: propertyAccessor('shipmentShipToAddress.shipToName'),
  },
  {
    header: 'Ship To Address Line 1',
    toStringFunction: propertyAccessor('shipmentShipToAddress.addressLine1Text'),
  },
  {
    header: 'Ship To Address Line 2',
    toStringFunction: propertyAccessor('shipmentShipToAddress.addressLine2Text'),
  },
  {
    header: 'Ship To Address Line 3',
    toStringFunction: propertyAccessor('shipmentShipToAddress.addressLine3Text'),
  },
  {
    header: 'Ship To City',
    toStringFunction: propertyAccessor('shipmentShipToAddress.cityName'),
  },
  {
    header: 'Ship To Zip',
    toStringFunction: propertyAccessor('shipmentShipToAddress.zipCode'),
  },
  {
    header: 'Ship To Country',
    toStringFunction: propertyAccessor('shipmentShipToAddress.countryCode'),
  },
  {
    header: 'Ship To Email',
    toStringFunction: propertyAccessor('shipmentShipToAddress.emailAddressText'),
  },
  {
    header: 'Ship To Phone',
    toStringFunction: propertyAccessor('shipmentShipToAddress.phoneNumber'),
  },
  {
    header: 'Carrier',
    toStringFunction: propertyAccessor('shipmentVendors.vendorCode'),
  },
  {
    header: 'Hub',
    toStringFunction: propertyAccessor('shipmentVendors.hubCode'),
  },
  {
    header: 'CMR',
    toStringFunction: (s: Shipment) => unique(get(s, 'proNumbers')),
  },
  {
    header: 'Appointment Number',
    toStringFunction: (s: Shipment) => unique(get(s, 'vendorAccessNumbers')),
  },
  {
    header: 'Trailer ID Reference Number',
    toStringFunction: (s: Shipment) => unique(get(s, 'transportHolderIdReferenceNumbers')),
  },
  {
    header: 'Trailer Number',
    toStringFunction: (s: Shipment) => unique(get(s, 'transportHolderNumbers')),
  },
  {
    header: 'Trailer License Plate Number',
    toStringFunction: (s: Shipment) => unique(get(s, 'transportHolderLicensePlateNumbers')),
  },
  {
    header: 'Trailer Seal Number',
    toStringFunction: (s: Shipment) => unique(get(s, 'transportHolderSealNumbers')),
  },
  {
    header: 'Traffic Instruction',
    toStringFunction: (s: Shipment) => s.deliveryInstructionText,
  },
  {
    header: '# Planned Cartons',
    toStringFunction: (s: Shipment) => s.plannedGoodsHolderCount,
  },
  {
    header: '# Actual Cartons',
    toStringFunction: (s: Shipment) => s.calculatedGoodsHolderCount,
  },
  {
    header: '# Planned Units',
    toStringFunction: (s: Shipment) => s.calculatedPlannedQuantity,
  },
  {
    header: '# Actual Units',
    toStringFunction: (s: Shipment) => s.calculatedPackedQuantity,
  },
  {
    header: 'Total Volume',
    toStringFunction: (s: Shipment) => measure(get(s, 'calculatedGrossVolume')),
  },
  {
    header: 'Total Weight',
    toStringFunction: (s: Shipment) => measure(get(s, 'calculatedGrossWeight')),
  },
  {
    header: 'Actual Planned Date',
    toStringFunction: (s: Shipment) => milestoneDate(s.actualShipmentPlannedDate),
  },
  {
    header: 'Planned Shipped Date',
    toStringFunction: (s: Shipment) => milestoneDate(get(s, 'plannedShipmentReadyTimestamp')),
  },
  {
    header: 'Actual Shipped Date',
    toStringFunction: (s: Shipment) => milestoneDate(s.actualShippedTimestamp),
  },
  {
    header: 'Planned Delivery Date',
    toStringFunction: (s: Shipment) =>
      getPlannedDeliveryDate({ shipment: s, dateFormat: milestoneDate, isExport: true }),
  },
  {
    header: 'Rebooked Delivery Date',
    toStringFunction: (s: Shipment) => getRebookingDate({ shipment: s }),
  },
  {
    header: 'Actual Delivered Date',
    toStringFunction: (s: Shipment) => milestoneDate(s.actualDeliveryDate),
  },
  {
    header: 'Transit Days',
    toStringFunction: propertyAccessor('eddCalculation.transitDays'),
  },
  {
    header: 'Status',
    toStringFunction: (s: Shipment) => shipmentStatus(s.calculatedShipmentStatusCode),
  },
  {
    header: 'IDP',
    toStringFunction: (s: Shipment) => (s.iDPIndicator ? 'Y' : 'N'),
  },
  {
    header: 'APL',
    toStringFunction: (s: Shipment, carriers) => {
      return findCarrier(
        carriers,
        String(get(s, 'shipmentVendors.vendorCode')),
        String(get(s, 'shipmentVendors.hubCode'))
      )?.aplIndicator
        ? 'Y'
        : 'N'
    },
  },
  {
    header: 'REBOOKED',
    toStringFunction: (s: Shipment) => (isRebooking(s.rebooking?.rebookingType) ? 'Y' : 'N'),
  },
  {
    header: 'HOLD',
    toStringFunction: (s: Shipment) => (isHold(s.rebooking?.rebookingType) ? 'Y' : 'N'),
  },
  {
    header: 'X-DOCK',
    toStringFunction: (s: Shipment) => (s.xDockFlag ? 'Y' : 'N'),
  },
  {
    header: 'Export',
    toStringFunction: (s: Shipment) => (s.exportIndicator ? 'Y' : 'N'),
  },
  {
    header: 'One-time-shipment',
    toStringFunction: (s: Shipment) =>
      s.shipmentShipToAddress?.addressOverrideIndicator ? 'Y' : 'N',
  },
  {
    header: 'Original IDP Date',
    toStringFunction: (s: Shipment) => (s.iDPIndicator ? date(s.iDPPlannedTimestamp) : ''),
  },
  {
    header: 'New IDP Date',
    toStringFunction: (s: Shipment) => date(s.rebooking?.iDPPlannedTimestamp) || '',
  },
  {
    header: 'T&T Exceptions',
    toStringFunction: (s: Shipment) =>
      s.ttExceptions?.reduce(
        (currentValue, e) =>
          `${e.code}: ${e.description}${currentValue ? '; ' : ''}${currentValue}`,
        ''
      ),
  },
]
export const shipmentByPacklistXlsxColumns: XlsxColumn[] = [
  { header: 'Packlist', toStringFunction: (s: Shipment) => s.packlistNumber },
  {
    header: 'Delivery Note',
    toStringFunction: (s: Shipment) => unique(get(s, 'deliveries.deliveryNoteNumber')),
  },
  ...shipmentXlsxColumns,
]
export const shipmentByDeliveryNoteXlsxColumns: XlsxColumn[] = [
  {
    header: 'Delivery Note',
    toStringFunction: (s: Shipment) => get(s, 'deliveryNoteNumber'),
  },
  { header: 'Packlist', toStringFunction: (s: Shipment) => s.packlistNumber },
  ...shipmentXlsxColumns,
]
export const shipmentByGoodsHolderXlsxColumns: XlsxColumn[] = [
  {
    header: 'Carton',
    toStringFunction: (gh: GoodsHolder) => get(gh, 'goodsHolderNumber'),
  },
  {
    header: 'Tracking Number',
    toStringFunction: (gh: GoodsHolder) => get(gh, 'goodsHolderTrackingNumber'),
  },
  {
    header: 'Sales Order Number',
    toStringFunction: (gh: GoodsHolder) => get(gh, 'salesOrderNumber'),
  },
  {
    header: 'Purchase Order Number',
    toStringFunction: (gh: GoodsHolder) => get(gh, 'purchaseOrderNumber'),
  },
  {
    header: 'Delivery Note',
    toStringFunction: (gh: GoodsHolder) => get(gh, 'deliveryNoteNumber'),
  },
  { header: 'Packlist', toStringFunction: (s: Shipment) => s.packlistNumber },

  {
    header: 'Carrier',
    toStringFunction: propertyAccessor('shipmentVendors.vendorCode'),
  },
  {
    header: 'Hub',
    toStringFunction: propertyAccessor('shipmentVendors.hubCode'),
  },
  {
    header: 'Status',
    toStringFunction: (gh: GoodsHolder) => shipmentStatus(gh.calculatedGoodsHolderStatusCode),
  },
  {
    header: 'Shipping Point',
    toStringFunction: (s: Shipment) => s.shippingPointCode,
  },
  {
    header: 'Shipping Point Name',
    toStringFunction: (s: Shipment) => getShippingPointName(Number(s.shippingPointCode)),
  },
  {
    header: 'Ship To',
    toStringFunction: propertyAccessor('shipmentShipToAddress.shipToCode'),
  },
  {
    header: 'Ship To Name',
    toStringFunction: propertyAccessor('shipmentShipToAddress.shipToName'),
  },
  {
    header: 'CMR',
    toStringFunction: (gh: GoodsHolder) => gh.transportHolder?.proNumber,
  },
  {
    header: 'CMR Date',
    toStringFunction: (gh: GoodsHolder) => milestoneDate(gh.transportHolder?.cmrTimestamp),
  },
  {
    header: 'Appointment Number',
    toStringFunction: (gh: GoodsHolder) => gh.transportHolder?.vendorAccessNumber,
  },
  {
    header: 'Trailer ID Reference Number',
    toStringFunction: (gh: GoodsHolder) => gh.transportHolder?.idReferenceNumber,
  },
  {
    header: 'Trailer Number',
    toStringFunction: (gh: GoodsHolder) => gh.transportHolder?.transportHolderNumber,
  },
  {
    header: 'Trailer License Plate Number',
    toStringFunction: (gh: GoodsHolder) => gh.transportHolder?.licensePlateNumber,
  },
  {
    header: 'Trailer Seal Number',
    toStringFunction: (gh: GoodsHolder) => gh.transportHolder?.sealNumber,
  },
  {
    header: 'Actual Planned Date',
    toStringFunction: (s: Shipment) => milestoneDate(s.actualShipmentPlannedDate),
  },
  {
    header: 'Planned Shipped Date',
    toStringFunction: (s: Shipment) => milestoneDate(get(s, 'plannedShipmentReadyTimestamp')),
  },
  {
    header: 'Actual Shipped Date',
    toStringFunction: (s: Shipment) => milestoneDate(s.actualShippedTimestamp),
  },
  {
    header: 'Planned Delivery Date',
    toStringFunction: (s: Shipment) =>
      getPlannedDeliveryDate({ shipment: s, dateFormat: milestoneDate, isExport: true }),
  },
  {
    header: 'Rebooked Delivery Date',
    toStringFunction: (s: Shipment) => getRebookingDate({ shipment: s }),
  },
  {
    header: 'Actual Delivered Date',
    toStringFunction: (s: Shipment) => milestoneDate(s.actualDeliveryDate),
  },
  {
    header: 'REBOOKED',
    toStringFunction: (s: Shipment) => (isRebooking(s.rebooking?.rebookingType) ? 'Y' : 'N'),
  },
  {
    header: 'Weight',
    toStringFunction: (gh: GoodsHolder) => measure(get(gh, 'goodsHolderMeasures.grossWeight')),
  },
  {
    header: 'Volume',
    toStringFunction: (gh: GoodsHolder) => measure(get(gh, 'goodsHolderMeasures.grossVolume')),
  },
  {
    header: '# Planned Units',
    toStringFunction: (gh: GoodsHolder) => gh.calculatedPlannedQuantity,
  },
  {
    header: '# Actual Units',
    toStringFunction: (gh: GoodsHolder) => gh.calculatedPackedQuantity,
  },
  {
    header: 'Direct Drop',
    toStringFunction: (gh: GoodsHolder) => (gh.transportHolder?.directDropIndicator ? 'Y' : 'N'),
  },
]
